/**
 * Shared labels and tooltip copy for the three token metrics surfaced across
 * the dashboard (see #441/#443). A single ambiguous "tokens" number used to
 * conflate three different concepts, dominated by cache_read (often ~95%+ of
 * the total) — these strings make the distinction explicit everywhere the
 * metric is shown.
 *
 * - Total tokens   = input + output + cache_creation + cache_read (all flow, incl. cache)
 * - Content tokens = input + output only (excludes cache overhead) — the "real work" number
 * - Cost tokens    = price-weighted sum (input=1x, output=5x, cache_create=1.25x, cache_read=0.1x)
 */

export const TOTAL_TOKENS_LABEL = 'Total Tokens';
export const CONTENT_TOKENS_LABEL = 'Content Tokens';
export const COST_TOKENS_LABEL = 'Cost Tokens';
export const CACHE_TOKENS_LABEL = 'Cache Tokens';

export const TOTAL_TOKENS_TOOLTIP =
  'Total tokens: all flow including cache (input + output + cache_creation + cache_read). Usually dominated by cache_read.';

export const CONTENT_TOKENS_TOOLTIP =
  'Content tokens: input + output only (excludes cache overhead) — the actual new content sent/generated.';

export const COST_TOKENS_TOOLTIP =
  'Cost-weighted tokens: input×1, output×5, cache_creation×1.25, cache_read×0.1 — approximates relative API spend.';

export const CACHE_TOKENS_TOOLTIP =
  'Cache tokens: cache_read (reused prompt prefixes) + cache_creation (prompt caching writes). This is the cache overhead excluded from content tokens.';

/** The four raw token counters carried on a session/sitting, as the int64
 * strings the API returns. */
export interface TokenCounts {
  inputTokens?: string;
  outputTokens?: string;
  cacheCreationTokens?: string;
  cacheReadTokens?: string;
}

/**
 * Sums the four raw counters into the Total-tokens figure defined above
 * (input + output + cache_creation + cache_read). Missing, blank, or
 * non-numeric fields count as zero. Shared by the Sessions table and the
 * SessionChapters vitals row so the arithmetic behind TOTAL_TOKENS_LABEL
 * lives in exactly one place.
 */
export function sumTotalTokens(t: TokenCounts): number {
  return (
    (parseInt(t.inputTokens || '0', 10) || 0) +
    (parseInt(t.outputTokens || '0', 10) || 0) +
    (parseInt(t.cacheCreationTokens || '0', 10) || 0) +
    (parseInt(t.cacheReadTokens || '0', 10) || 0)
  );
}

/**
 * Relative price weights behind the Cost-tokens figure: output ~5× input,
 * cache_creation ~1.25×, cache_read ~0.1× (see the Cost tokens line above and
 * COST_TOKENS_TOOLTIP). Kept as one named constant so the arithmetic in
 * sumCostTokens and the tooltip/footnote copy can't silently drift apart — a
 * changed weight here is the single source of truth. Keep COST_TOKENS_TOOLTIP
 * (and the SessionDetail "Billed = …" footnote) in sync if these change.
 */
export const COST_TOKEN_WEIGHTS = {
  input: 1,
  output: 5,
  cacheCreation: 1.25,
  cacheRead: 0.1,
} as const;

/**
 * Price-weighted token sum (the Cost-tokens figure): each raw counter scaled by
 * its COST_TOKEN_WEIGHTS factor, approximating relative API spend. Missing,
 * blank, or non-numeric fields count as zero. The weighting is linear, so
 * summing per-source results (session + subagents) equals weighting the combined
 * totals — callers may add the sources independently.
 */
export function sumCostTokens(t: TokenCounts): number {
  return (
    (parseInt(t.inputTokens || '0', 10) || 0) * COST_TOKEN_WEIGHTS.input +
    (parseInt(t.outputTokens || '0', 10) || 0) * COST_TOKEN_WEIGHTS.output +
    (parseInt(t.cacheCreationTokens || '0', 10) || 0) * COST_TOKEN_WEIGHTS.cacheCreation +
    (parseInt(t.cacheReadTokens || '0', 10) || 0) * COST_TOKEN_WEIGHTS.cacheRead
  );
}
